import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { usePropertyMutations } from '@/hooks/usePropertyMutations';
import { usePropertyFeatureMutations } from '@/hooks/usePropertyFeatureMutations';
import { usePropertyHighlightMutations } from '@/hooks/usePropertyHighlightMutations';
import { useProperty } from '@/hooks/useProperties';
import { usePropertyFeatureIds } from '@/hooks/useFeatures';
import { useHosts } from '@/hooks/useHosts';
import { useNeighborhoods } from '@/hooks/useNeighborhoods';
import { useNavigate } from 'react-router-dom';
import PropertyBasicInfo from './PropertyBasicInfo';
import PropertyDetailsForm from './PropertyDetailsForm';
import PropertyFeaturesSelector from './PropertyFeaturesSelector';
import PropertyImagesForm from './PropertyImagesForm';
import PropertyHostSelector from './PropertyHostSelector';
import PropertyNeighborhoodSelector from './PropertyNeighborhoodSelector';
import PropertyHighlightsForm from './PropertyHighlightsForm';
import { Host } from '@/hooks/useHosts';
import { Neighborhood } from '@/hooks/useNeighborhoods';

interface PropertyHighlight {
  title: string;
  description: string;
}

interface EditPropertyFormProps {
  propertyId: string;
}

const EditPropertyForm = ({ propertyId }: EditPropertyFormProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { updateProperty } = usePropertyMutations();
  const { updatePropertyFeatures } = usePropertyFeatureMutations();
  const { createPropertyHighlights } = usePropertyHighlightMutations();
  const { data: property, isLoading } = useProperty(propertyId);
  const { data: existingFeatureIds = [], isLoading: isLoadingFeatures } = usePropertyFeatureIds(propertyId);
  const { data: hosts = [] } = useHosts();
  const { data: neighborhoods = [] } = useNeighborhoods();

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    shortName: '',
    shortDescription: '',
    longDescription: '',
    airbnbUrl: '',
    price: 0,
    rating: 0,
    guests: 1,
    bedrooms: 0,
    bathrooms: 0,
    area: 0,
    latitude: 0,
    longitude: 0,
    available: true,
    featured: false,
  });

  const [images, setImages] = useState<string[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [selectedHost, setSelectedHost] = useState<Host | null>(null);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<Neighborhood | null>(null);
  const [highlights, setHighlights] = useState<PropertyHighlight[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load property data when available
  useEffect(() => {
    if (property) {
      console.log('🏠 Loading property for editing:', property);
      
      setFormData({
        title: property.title || '',
        shortName: property.shortName || '',
        shortDescription: property.shortDescription || '',
        longDescription: property.longDescription || '',
        airbnbUrl: property.airbnbUrl || '',
        price: property.price || 0,
        rating: property.rating || 0,
        guests: property.guests || 1,
        bedrooms: property.bedrooms || 0,
        bathrooms: property.bathrooms || 0,
        area: property.area || 0,
        latitude: property.latitude || 0,
        longitude: property.longitude || 0,
        available: property.available ?? true,
        featured: property.featured ?? false,
      });

      setImages(property.images || []);
      
      // Load highlights
      if (property.propertyHighlights) {
        setHighlights(property.propertyHighlights.map(h => ({
          title: h.title,
          description: h.description
        })));
      }
    }
  }, [property]);

  // Load host and neighborhood when data is available
  useEffect(() => {
    if (property && hosts.length > 0 && neighborhoods.length > 0) {
      console.log('🔍 Setting host and neighborhood from property data');
      console.log('🔍 Property hostId:', property.hostId);
      console.log('🔍 Property neighborhoodId:', property.neighborhoodId);
      
      // Find and set host using the hostId from property
      if (property.hostId) {
        const foundHost = hosts.find(h => h.id === property.hostId);
        if (foundHost) {
          console.log('✅ Found and setting host:', foundHost);
          setSelectedHost(foundHost);
        } else {
          console.log('❌ Host not found with ID:', property.hostId);
        }
      }

      // Find and set neighborhood using the neighborhoodId from property
      if (property.neighborhoodId) {
        const foundNeighborhood = neighborhoods.find(n => n.id === property.neighborhoodId);
        if (foundNeighborhood) {
          console.log('✅ Found and setting neighborhood:', foundNeighborhood);
          setSelectedNeighborhood(foundNeighborhood);
        } else {
          console.log('❌ Neighborhood not found with ID:', property.neighborhoodId);
        }
      }
    }
  }, [property, hosts, neighborhoods]);

  // Load existing features when available
  useEffect(() => {
    if (existingFeatureIds.length > 0) {
      console.log('🏷️ Loading existing features for property:', existingFeatureIds);
      setSelectedFeatures(existingFeatureIds);
    }
  }, [existingFeatureIds]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFeatureToggle = (featureId: string, checked: boolean) => {
    setSelectedFeatures(prev => 
      checked 
        ? [...prev, featureId]
        : prev.filter(id => id !== featureId)
    );
  };

  const validateHighlights = () => {
    if (highlights.length === 0) {
      toast({
        title: "Error",
        description: "Debe agregar al menos una característica destacada",
        variant: "destructive",
      });
      return false;
    }

    for (let i = 0; i < highlights.length; i++) {
      const highlight = highlights[i];
      if (!highlight.title.trim()) {
        toast({
          title: "Error",
          description: `El título de la característica ${i + 1} es requerido`,
          variant: "destructive",
        });
        return false;
      }
      if (!highlight.description.trim()) {
        toast({
          title: "Error",
          description: `La descripción de la característica ${i + 1} es requerida`,
          variant: "destructive",
        });
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validations
    if (!formData.title.trim()) {
      toast({
        title: "Error",
        description: "El título es requerido",
        variant: "destructive",
      });
      return;
    }

    if (!formData.shortName.trim()) {
      toast({
        title: "Error",
        description: "El nombre corto es requerido",
        variant: "destructive",
      });
      return;
    }

    if (!formData.shortDescription.trim()) {
      toast({
        title: "Error",
        description: "La descripción corta es requerida",
        variant: "destructive",
      });
      return;
    }

    if (formData.price <= 0) {
      toast({
        title: "Error",
        description: "El precio debe ser mayor a 0",
        variant: "destructive",
      });
      return;
    }

    if (images.length === 0) {
      toast({
        title: "Error",
        description: "Debe agregar al menos una imagen",
        variant: "destructive",
      });
      return;
    }

    if (!validateHighlights()) {
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('🔄 Submitting property update with data:', {
        ...formData,
        hostId: selectedHost?.id || null,
        neighborhoodId: selectedNeighborhood?.id || null,
        features: selectedFeatures,
        highlights
      });

      // Actualizar la propiedad
      await updateProperty.mutateAsync({
        id: propertyId,
        ...formData,
        images,
        hostId: selectedHost?.id || null,
        neighborhoodId: selectedNeighborhood?.id || null,
      });

      // Update property features
      await updatePropertyFeatures.mutateAsync({
        propertyId,
        featureIds: selectedFeatures,
      });

      // Actualizar los highlights de la propiedad
      if (highlights.length > 0) {
        await createPropertyHighlights.mutateAsync({
          propertyId,
          highlights,
        });
      }
      
      toast({
        title: "Propiedad actualizada",
        description: "La propiedad ha sido actualizada exitosamente",
      });

      navigate('/admin');
    } catch (error) {
      console.error('Error updating property:', error);
      toast({
        title: "Error",
        description: "Error al actualizar la propiedad. Por favor intenta de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || isLoadingFeatures) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando propiedad...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Propiedad no encontrada</p>
        <Button
          onClick={() => navigate('/admin')}
          className="mt-4"
        >
          Volver al Admin
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>Información Básica</CardTitle>
        </CardHeader>
        <CardContent>
          <PropertyBasicInfo
            formData={formData}
            onInputChange={handleInputChange}
          />
        </CardContent>
      </Card>

      {/* Property Details */}
      <Card>
        <CardHeader>
          <CardTitle>Detalles de la Propiedad</CardTitle>
        </CardHeader>
        <CardContent>
          <PropertyDetailsForm
            formData={formData}
            onInputChange={handleInputChange}
          />
        </CardContent>
      </Card>

      {/* Property Highlights */}
      <Card>
        <CardHeader>
          <CardTitle>Características Destacadas</CardTitle>
        </CardHeader>
        <CardContent>
          <PropertyHighlightsForm
            highlights={highlights}
            onHighlightsChange={setHighlights}
          />
        </CardContent>
      </Card>

      {/* Location */}
      <Card>
        <CardHeader>
          <CardTitle>Ubicación y Anfitrión</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <PropertyHostSelector
              selectedHost={selectedHost}
              onHostSelect={setSelectedHost}
            />
            <PropertyNeighborhoodSelector
              selectedNeighborhood={selectedNeighborhood}
              onNeighborhoodSelect={setSelectedNeighborhood}
            />
          </div>
        </CardContent>
      </Card>

      {/* Features */}
      <Card>
        <CardHeader>
          <CardTitle>Amenidades</CardTitle>
        </CardHeader>
        <CardContent>
          <PropertyFeaturesSelector
            selectedFeatures={selectedFeatures}
            onFeatureToggle={handleFeatureToggle}
          />
        </CardContent>
      </Card>

      {/* Images */}
      <Card>
        <CardHeader>
          <CardTitle>Imágenes</CardTitle>
        </CardHeader>
        <CardContent>
          <PropertyImagesForm
            images={images}
            onImagesChange={setImages}
          />
        </CardContent>
      </Card>

      {/* Options */}
      <Card>
        <CardHeader>
          <CardTitle>Opciones</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="available"
                checked={formData.available}
                onCheckedChange={(checked) => handleInputChange('available', checked)}
              />
              <label htmlFor="available" className="text-sm font-medium">
                Propiedad disponible
              </label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="featured"
                checked={formData.featured}
                onCheckedChange={(checked) => handleInputChange('featured', checked)}
              />
              <label htmlFor="featured" className="text-sm font-medium">
                Propiedad destacada
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Submit Actions */}
      <div className="flex justify-end space-x-4 pt-6 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/admin')}
          disabled={isSubmitting}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </div>
    </form>
  );
};

export default EditPropertyForm;
